import { useCallback, useEffect, useRef, useState } from 'react'

import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { isPending, isFailed } from 'redux/toolkit/api'
import {
  refreshAccessToken,
  validateSession,
  setOAuthState,
  hydrateFromStorage
} from 'redux/features/bccAuth/bccAuthSlice'
import {
  isTokenExpired,
  generateState,
  buildAuthorizationUrl,
  generateCodeVerifier,
  generateCodeChallenge,
  storeCodeVerifier,
  getStoredAuthDismissed,
  storeAuthDismissed,
  getStoredAccessToken
} from 'lib/oauth2/bccOAuth2Utils'
import appConfig from 'config/appConfig'
import logger from 'lib/logger'

// Session validation interval: 15 minutes in milliseconds
const SESSION_VALIDATION_INTERVAL_MS = 15 * 60 * 1000

export interface UseBccAuthTokenOptions {
  autoTriggerAuth?: boolean
}

export interface UseBccAuthTokenResult {
  token: string | undefined
  isAuthenticated: boolean
  needsAuth: boolean
  isRefreshing: boolean
  refreshFailed: boolean
  dismissAuth: () => void
  isEnabled: boolean
  isReauthentication: boolean
}

export function useBccAuthToken(options?: UseBccAuthTokenOptions): UseBccAuthTokenResult {
  const dispatch = useAppDispatch()
  const [authDismissed, setAuthDismissed] = useState(getStoredAuthDismissed)
  const validationIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastValidationRef = useRef<number>(0)
  const autoAuthInitiatedRef = useRef<boolean>(false)

  const {
    accessToken,
    expiresAt,
    sessionSecret,
    isRefreshing,
    refreshFailed,
    isValidatingSession,
    sessionValidationFailed
  } = useAppSelector(_store => ({
    accessToken: _store.bccAuth.accessToken,
    expiresAt: _store.bccAuth.expiresAt,
    sessionSecret: _store.bccAuth.sessionSecret,
    isRefreshing: isPending(_store.bccAuth.api.refreshTokenStatus),
    refreshFailed: isFailed(_store.bccAuth.api.refreshTokenStatus),
    isValidatingSession: isPending(_store.bccAuth.api.sessionValidationStatus),
    sessionValidationFailed: isFailed(_store.bccAuth.api.sessionValidationStatus)
  }))

  const { isEnabled } = appConfig.BCC_LOGIN_APP

  // Check if token is valid (exists and not expired)
  const tokenExpired = isTokenExpired(expiresAt)
  const isAuthenticated = !!accessToken && !tokenExpired

  // Determine if auth is needed (token missing or expired, or session invalid, and not refreshing, and not dismissed)
  const needsAuth = isEnabled && (!isAuthenticated || sessionValidationFailed) && !isRefreshing && !authDismissed

  // Hydrate auth state from sessionStorage on first mount
  useEffect(() => {
    if (isEnabled) {
      dispatch(hydrateFromStorage())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-refresh token when it's about to expire
  useEffect(() => {
    if (!isEnabled || !accessToken) {
      return
    }

    if (tokenExpired && !isRefreshing && !refreshFailed && sessionSecret) {
      dispatch(refreshAccessToken({ sessionSecret }))
    }
  }, [isEnabled, accessToken, tokenExpired, isRefreshing, refreshFailed, sessionSecret, dispatch])

  // Session validation helper
  const performSessionValidation = useCallback(() => {
    if (!sessionSecret || isValidatingSession) {
      return
    }

    // Avoid validating too frequently (at least 1 minute between validations)
    const now = Date.now()
    if (now - lastValidationRef.current < 60 * 1000) {
      return
    }

    lastValidationRef.current = now
    dispatch(validateSession({ sessionSecret }))
  }, [sessionSecret, isValidatingSession, dispatch])

  // Session validation on initial load and every 15 minutes
  useEffect(() => {
    if (!isEnabled || !sessionSecret) {
      // Clear interval if session secret is not available
      if (validationIntervalRef.current) {
        clearInterval(validationIntervalRef.current)
        validationIntervalRef.current = null
      }
      return undefined
    }

    // Validate on initial load
    performSessionValidation()

    // Set up 15-minute interval validation
    validationIntervalRef.current = setInterval(() => {
      performSessionValidation()
    }, SESSION_VALIDATION_INTERVAL_MS)

    return () => {
      if (validationIntervalRef.current) {
        clearInterval(validationIntervalRef.current)
        validationIntervalRef.current = null
      }
    }
  }, [isEnabled, sessionSecret, performSessionValidation])

  // Session validation on window focus
  useEffect(() => {
    if (!isEnabled || !sessionSecret) {
      return undefined
    }

    const handleFocus = () => {
      performSessionValidation()
    }

    window.addEventListener('focus', handleFocus)

    return () => {
      window.removeEventListener('focus', handleFocus)
    }
  }, [isEnabled, sessionSecret, performSessionValidation])

  // Auto-trigger OAuth2 auth when needed (no popup required)
  // Note: authDismissed is intentionally NOT checked here - with auto-auth enabled,
  // we always attempt auth if no valid token exists. The dismissed flag only applied
  // to the old popup UX where users could explicitly defer authentication.
  useEffect(() => {
    if (!isEnabled || isRefreshing) {
      return
    }
    if (options?.autoTriggerAuth !== true) {
      return
    }

    // Prevent multiple triggers - only initiate once per component lifecycle
    if (autoAuthInitiatedRef.current) {
      return
    }

    // Check sessionStorage directly to avoid race condition with Redux hydration
    const storedToken = getStoredAccessToken()
    if (storedToken.token && storedToken.expiresAt && !isTokenExpired(storedToken.expiresAt)) {
      return
    }

    // Mark as initiated before async operation to prevent re-entry
    autoAuthInitiatedRef.current = true

    // Trigger auth initiation
    const initiateAuthFlow = async () => {
      try {
        const returnPath = window.location.pathname + window.location.search + window.location.hash
        const state = generateState(returnPath)
        const codeVerifier = generateCodeVerifier()
        const codeChallenge = await generateCodeChallenge(codeVerifier)

        dispatch(setOAuthState(state))
        storeCodeVerifier(codeVerifier)

        const authUrl = buildAuthorizationUrl({ state, codeChallenge })
        window.location.href = authUrl
      } catch (error) {
        logger.error(`[BCC OAuth2] Failed to auto-initiate authentication: ${error}`)
        // Reset flag on error so retry is possible
        autoAuthInitiatedRef.current = false
      }
    }

    initiateAuthFlow()
  }, [isEnabled, isRefreshing, options?.autoTriggerAuth, dispatch])

  // Dismiss auth prompt for this tab session
  const dismissAuth = useCallback(() => {
    setAuthDismissed(true)
    storeAuthDismissed()
  }, [])

  return {
    token: isAuthenticated ? accessToken : undefined,
    isAuthenticated,
    needsAuth,
    isRefreshing,
    refreshFailed,
    dismissAuth,
    isEnabled,
    isReauthentication: refreshFailed || sessionValidationFailed
  }
}

export default useBccAuthToken
